import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  MessageSquare, X, Minus, Maximize2, Minimize2, 
  Send, Paperclip, Smile, Search, 
  CheckCheck,
  PanelLeftClose, PanelLeft
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions, MODULES, ACTIONS } from '../../hooks/usePermissions';
import { messageService } from '../../services/erpApi';
import Avatar from '../Avatar/Avatar';
import './FloatingChat.css';

const FloatingChat = () => {
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canAccessMessages = !permissionsLoading && hasPermission(MODULES.MESSAGES, ACTIONS.VIEW);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isWide, setIsWide] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const scrollRef = useRef(null);

  // Formatar hora para mensagens
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }, []);

  // Carregar conversas
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm || undefined
      };
      const response = await messageService.getConversations(filters);
      if (response.success) {
        setConversations(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Carregar contador de não lidas
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await messageService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data?.count || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar contador de não lidas:', error);
    }
  }, []);

  // Carregar mensagens de uma conversa
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const response = await messageService.getMessages(conversationId);
      if (response.success) {
        setMessages(response.data || []);
        // Marcar como lida
        await messageService.markConversationAsRead(conversationId);
        // Atualizar contador de não lidas
        await loadUnreadCount();
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  }, [loadUnreadCount]);

  // Selecionar conversa
  const handleSelectConversation = useCallback(async (conversationId) => {
    setSelectedConversationId(conversationId);
    await loadMessages(conversationId);
    setShowSidebar(false);
  }, [loadMessages]);

  // Enviar mensagem
  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedConversationId || sending) return;

    try {
      setSending(true);
      const response = await messageService.sendMessage(selectedConversationId, message.trim());
      if (response.success) {
        setMessage('');
        // Recarregar mensagens
        await loadMessages(selectedConversationId);
        // Recarregar conversas para atualizar última mensagem
        await loadConversations();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setSending(false);
    }
  };

  // Scroll automático
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isMinimized]);

  // Carregar conversas ao montar e quando filtros mudarem
  useEffect(() => {
    if (!canAccessMessages) return;
    loadConversations();
  }, [loadConversations, canAccessMessages]);

  // Carregar contador de não lidas ao montar
  useEffect(() => {
    if (!canAccessMessages) return;

    loadUnreadCount();
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount, canAccessMessages]);

  // Polling para atualizar mensagens automaticamente
  useEffect(() => {
    if (!canAccessMessages || !selectedConversationId || !isOpen) return;

    const interval = setInterval(async () => {
      await loadMessages(selectedConversationId);
      await loadConversations();
    }, 5000); // Atualizar a cada 5 segundos

    return () => clearInterval(interval);
  }, [selectedConversationId, isOpen, loadMessages, loadConversations, canAccessMessages]);

  if (!canAccessMessages) {
    return null;
  }

  // Obter conversa ativa
  const activeConv = conversations.find(c => c.id === selectedConversationId);

  // Obter primeiro participante (outro usuário)
  const getOtherParticipant = (conversation) => {
    if (!conversation?.participants || conversation.participants.length === 0) {
      return { name: 'Usuário', avatar_url: null };
    }
    // Filtrar o usuário atual
    const other = conversation.participants.find(p => p.id !== user?.id);
    return other || conversation.participants[0];
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="floating-chat-toggle"
      >
        {unreadCount > 0 && (
          <div className="floating-chat-toggle-ping" />
        )}
        <MessageSquare size={20} className="floating-chat-toggle-icon" />
        {unreadCount > 0 && (
          <span className="floating-chat-toggle-badge">{unreadCount}</span>
        )}
      </button>
    );
  }

  return (
    <div 
      className={`floating-chat-container ${
        isMinimized ? 'floating-chat-minimized' : isWide ? 'floating-chat-wide' : ''
      }`}
    >
      {/* Container Principal Glassmorphism */}
      <div className="floating-chat-main">
        
        {/* SIDEBAR OCULTA (Compartimento de Troca de Mensagem) */}
        <div 
          className={`floating-chat-sidebar ${
            showSidebar ? 'floating-chat-sidebar-open' : ''
          }`}
        >
          <div className="floating-chat-sidebar-header">
             <div className="floating-chat-search-wrapper">
                <Search size={14} className="floating-chat-search-icon" />
                <input 
                  type="text" 
                  placeholder="Buscar conversa..." 
                  className="floating-chat-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>
          </div>
          <div className="floating-chat-sidebar-content">
             {loading ? (
               <div className="floating-chat-loading">Carregando...</div>
             ) : conversations.length === 0 ? (
               <div className="floating-chat-empty">
                 <p>Nenhuma conversa encontrada</p>
               </div>
             ) : (
               conversations.map(conv => {
                 const otherParticipant = getOtherParticipant(conv);
                 const isActive = selectedConversationId === conv.id;
                 return (
                   <button 
                     key={conv.id}
                     onClick={() => handleSelectConversation(conv.id)}
                     className={`floating-chat-conv-item ${
                       isActive ? 'floating-chat-conv-item-active' : ''
                     }`}
                   >
                     <div className="floating-chat-conv-avatar-wrapper">
                       <Avatar 
                         name={otherParticipant.name} 
                         imageUrl={otherParticipant.avatar_url} 
                         size="sm" 
                       />
                     </div>
                     <div className="floating-chat-conv-info">
                        <p className="floating-chat-conv-name">#{conv.subject}</p>
                        <p className="floating-chat-conv-dept">
                          {otherParticipant.name}
                        </p>
                     </div>
                     {conv.unread_count > 0 && (
                       <div className="floating-chat-unread-badge" />
                     )}
                   </button>
                 );
               })
             )}
          </div>
        </div>

        {/* ÁREA DE CHAT (Ocupa o modal inteiro por padrão) */}
        <div className="floating-chat-area">
          
          {/* Header do Chat */}
          <div className="floating-chat-header">
             <div className="floating-chat-header-left">
                <button 
                  onClick={() => setShowSidebar(!showSidebar)}
                  className={`floating-chat-header-button ${
                    showSidebar ? 'floating-chat-header-button-active' : ''
                  }`}
                  title="Trocar Conversa"
                >
                  {showSidebar ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
                </button>
                
                {activeConv ? (
                  <>
                    <div className="floating-chat-header-user">
                       <div className="floating-chat-header-avatar-wrapper">
                          {(() => {
                            const otherParticipant = getOtherParticipant(activeConv);
                            return (
                              <Avatar 
                                name={otherParticipant.name} 
                                imageUrl={otherParticipant.avatar_url} 
                                size="sm" 
                              />
                            );
                          })()}
                       </div>
                       <div className="floating-chat-header-user-info">
                          <h4 className="floating-chat-header-user-name">
                            #{activeConv.subject}
                          </h4>
                          <p className="floating-chat-header-user-status">
                            {(() => {
                              const otherParticipant = getOtherParticipant(activeConv);
                              return otherParticipant.name;
                            })()}
                          </p>
                       </div>
                    </div>
                  </>
                ) : (
                  <div className="floating-chat-header-user">
                    <div className="floating-chat-header-user-info">
                      <h4 className="floating-chat-header-user-name">Selecione uma conversa</h4>
                    </div>
                  </div>
                )}
             </div>

             <div className="floating-chat-header-actions">
                {!isMinimized && (
                   <button 
                     onClick={() => setIsWide(!isWide)} 
                     className="floating-chat-header-action"
                   >
                     {isWide ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                   </button>
                )}
                <button 
                  onClick={() => {
                    setIsMinimized(!isMinimized);
                    if (!isMinimized) {
                      // Fechar sidebar ao minimizar
                      setShowSidebar(false);
                    }
                  }} 
                  className="floating-chat-header-action floating-chat-header-action-minimize"
                >
                  <Minus size={18} />
                </button>
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="floating-chat-header-action floating-chat-header-action-close"
                >
                  <X size={18} />
                </button>
             </div>
          </div>

          {/* Área de Mensagens */}
          {!isMinimized && (
            <>
              {activeConv ? (
                <>
                  <div 
                    ref={scrollRef}
                    className="floating-chat-messages"
                  >
                    {messages.length === 0 ? (
                      <div className="floating-chat-messages-empty">
                        <span>Início da Conversa Profissional</span>
                      </div>
                    ) : (
                      <>
                        <div className="floating-chat-date-divider">
                          <span className="floating-chat-date-badge">
                            Hoje, {new Date().toLocaleDateString('pt-BR')}
                          </span>
                        </div>

                        {messages.map((msg) => {
                          const isMe = msg.sender_id === user?.id;
                          return (
                            <div 
                              key={msg.id} 
                              className={`floating-chat-message ${
                                isMe ? 'floating-chat-message-me' : 'floating-chat-message-other'
                              }`}
                            >
                              <div className={`floating-chat-message-content ${
                                isMe ? 'floating-chat-message-content-me' : 'floating-chat-message-content-other'
                              }`}>
                                <div className={`floating-chat-message-bubble ${
                                  isMe 
                                  ? 'floating-chat-message-bubble-me' 
                                  : 'floating-chat-message-bubble-other'
                                }`}>
                                  {msg.content}
                                </div>
                                <div className="floating-chat-message-meta">
                                  <span className="floating-chat-message-time">
                                    {formatTime(msg.created_at)}
                                  </span>
                                  {isMe && <CheckCheck size={10} className="floating-chat-message-check" />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>

                  {/* Input de Mensagem */}
                  <form onSubmit={handleSend} className="floating-chat-input-form">
                     <div className="floating-chat-input-wrapper">
                        <button 
                          type="button" 
                          className="floating-chat-input-action"
                        >
                          <Paperclip size={18} />
                        </button>
                        <textarea 
                          rows={1}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend(e);
                            }
                          }}
                          placeholder="Mensagem interna..."
                          className="floating-chat-input"
                          disabled={sending}
                        />
                        <button 
                          type="button" 
                          className="floating-chat-input-action"
                        >
                          <Smile size={18} />
                        </button>
                        <button 
                          type="submit"
                          disabled={!message.trim() || sending}
                          className={`floating-chat-send-button ${
                            message.trim() ? 'floating-chat-send-button-active' : ''
                          }`}
                        >
                          <Send size={18} />
                        </button>
                     </div>
                  </form>
                </>
              ) : (
                <div className="floating-chat-messages-empty">
                  <MessageSquare size={48} />
                  <p>Selecione uma conversa para começar</p>
                </div>
              )}
            </>
          )}

          {/* View Minimizado */}
          {isMinimized && (
            <div className="floating-chat-minimized-view">
               <p className="floating-chat-minimized-text">
                 {activeConv 
                   ? `Aguardando resposta de #${activeConv.subject}...`
                   : 'Nenhuma conversa selecionada'}
               </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default FloatingChat;
